// Static placeholder content for the AI Coach library when the user has
// no saved sessions yet. Lives in /lib (not inside Coach.tsx) so the
// page component stays focused on flow logic, and so a returning user
// who deleted every session can be shown a true empty state instead of
// these fake rows.
//
// `SAMPLE_LIBRARY` powers the welcome cards on first visit.
// `SAMPLE_TAILORED` provides a fallback tailored plan when the AI call
// hasn't returned yet (or returned an empty payload).

export interface CoachSampleSession {
  id: string;
  customer: string;
  industry: string;
  sourcePlaybookTitle: string;
  meetingDate: string;
  attendees: number;
  status: "ready" | "draft";
  updated: string;
}

export interface CoachSampleBeat {
  id: string;
  title: string;
  original: string;
  tailored: string;
  annotation: string;
}

export interface CoachSampleTailored {
  hook: string;
  beats: CoachSampleBeat[];
  landing: string;
}

export const SAMPLE_LIBRARY: ReadonlyArray<CoachSampleSession> = [
  {
    id: "coach_halo_qbr",
    customer: "Halo Power Solutions",
    industry: "Solar / Clean Energy",
    sourcePlaybookTitle: "Cirrus exec pitch",
    meetingDate: "Nov 12, 2:00 PM PT",
    attendees: 3,
    status: "ready",
    updated: "1d ago",
  },
  {
    id: "coach_meridian_director",
    customer: "Meridian Health · Director sync",
    industry: "Healthcare",
    sourcePlaybookTitle: "NTO Service transformation",
    meetingDate: "Nov 18, 10:00 AM ET",
    attendees: 4,
    status: "draft",
    updated: "3d ago",
  },
];

export const SAMPLE_TAILORED: CoachSampleTailored = {
  hook: "Priya, last quarter you told your board forecast would land at $48M — it came in at $37M. The difference wasn't effort, it was signal. I want to show you in 35 minutes what real-time pipeline signal looks like for a company that sells the way Halo sells.",
  beats: [
    {
      id: "intro",
      title: "Open on Priya's forecast miss",
      original:
        "Open with the standard CRO pain — reps, dashboards, missed quota.",
      tailored:
        "Open with the Q3 forecast gap Priya mentioned on the discovery call ($48M forecast → $37M actual). Anchor the demo to closing that specific gap. Reference Halo's 380 open opps directly.",
      annotation:
        "Personalized to Priya's CFO lens — leads with the miss she raised, not a generic pain.",
    },
    {
      id: "pipeline_health",
      title: "Pipeline health — live",
      original:
        "Walk through the Pipeline Health dashboard, show risk scores on 380 open opps.",
      tailored:
        "Walk Pipeline Health. Dana will know the schema — invite her to challenge the risk score logic. When Marcus asks about rep adoption, pivot to the in-flow coaching card.",
      annotation:
        "Adds a Dana-specific invitation and a Marcus-pivot on adoption.",
    },
    {
      id: "forecast_accuracy",
      title: "Forecast accuracy — the wedge",
      original:
        "Show forecast accuracy improvement narrative for an exec audience.",
      tailored:
        "Walk the Commit → Best-Case → Closed walk. End with the 1.2% vs 11% variance line. Loop Priya in by name: 'this is the number you'd be writing to the board.'",
      annotation: "Frames the punchline directly at Priya's role.",
    },
  ],
  landing:
    "If we close that $11M gap once, this pays for itself before quarter-end. If we close it every quarter, Halo runs forecast like a public company — and that's the conversation your board has been waiting for.",
};

// Persisted in localStorage so a user who deletes every session and
// comes back is shown the real empty state, not the fake samples again.
const FIRST_VISIT_KEY = "qb_coach_first_visit_done_v1";

export function isCoachFirstVisit(): boolean {
  try {
    return window.localStorage.getItem(FIRST_VISIT_KEY) !== "1";
  } catch {
    return true;
  }
}

export function markCoachFirstVisitDone(): void {
  try {
    window.localStorage.setItem(FIRST_VISIT_KEY, "1");
  } catch {
    /* localStorage unavailable — proceed silently. */
  }
}
